// lib
import { useContext, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import { DataContext } from '@aytm/app/stats/page/question-views/cq/Component'

// components
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'

import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import { itemTemplate, valueTemplate, optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

const Batch = ({ item, position }) => {
  const { data, disabled, dispatch } = useContext(DataContext)

  const [changed, setChanged] = useState(false)

  const [selectedValue, setSelectedValue] = useState(null)
  const [selectedValues, setSelectedValues] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)

  const options = useMemo(
    () =>
      data.options.map(option => {
        return {
          ...option,
          items: option.items.filter(item => !data.excluded[position - 1].includes(item.questionType))
        }
      }),
    [position, data.options, data.excluded]
  )

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const buttonLabelTemplate = event => {
    const text =
      1 < event.options.length && event.options.length == event.value.length
        ? 'All'
        : 0 < event.value.length
        ? 1 == event.value.length
          ? event.options.find(o => o.key == event.value[0])?.text || ''
          : `Filtered to ${event.value.length}`
        : 'Select...'
    return <span dangerouslySetInnerHTML={{ __html: text }}></span>
  }

  const onSelectedChange = (value = null, _values) => {
    const option = options.flatMap(v => v.items).find(v => v.key == value) || null
    const values = _values || (null !== option ? option.options.map(v => v.key) : [])
    values.sort((a, b) => option.options.findIndex(v => v.key == a) - option.options.findIndex(v => v.key == b))

    setSelectedValue(value)
    setSelectedValues(values)
    setSelectedOption(option)

    setChanged(true)

    const newItem = {
      ...item,
      pattern: null !== option ? option.pattern || option.key : '',
      answerVariants: values.map(v => {
        const o = option.options.find(_v => _v.key == v)
        const av = { pattern: o.pattern }
        if (Object.hasOwn(o, 'base')) av.base = o.base
        return av
      })
    }

    if (Object.hasOwn(option, 'patternType')) newItem.patternType = option.patternType
    if (Object.hasOwn(option, 'base')) newItem.base = option.base

    dispatch({
      type: 'UPDATE_BATCH',
      data: {
        item: newItem,
        position: position - 1
      }
    })
  }

  useEffect(() => {
    if (changed) {
      setChanged(false)
      return
    }

    if (item.pattern == undefined || !item.pattern.length) {
      setSelectedValue(null)
      setSelectedValues([])
      setSelectedOption(null)
    } else {
      const option =
        options
          .flatMap(v => v.items)
          .find(v => String(v.pattern || v.key).toLowerCase() == item.pattern.toLowerCase()) || null

      if (null === option) {
        setSelectedValue(null)
        setSelectedValues([])
        setSelectedOption(null)
      } else {
        const value = option.key
        const values = option.options
          .filter(v => item.answerVariants.map(av => av.pattern).includes(v.pattern))
          .map(v => v.key)
        values.sort((a, b) => option.options.findIndex(v => v.key == a) - option.options.findIndex(v => v.key == b))

        setSelectedValue(value)
        setSelectedValues(values)
        setSelectedOption(option)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.pattern, item.answerVariants])

  useEffect(() => {
    const option =
      options.flatMap(v => v.items).find(v => String(v.pattern || v.key).toLowerCase() == item.pattern.toLowerCase()) ||
      null

    if (null === option) {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `b${position}.pattern`,
          logicData: null
        }
      })
    } else {
      const value = option.key
      const values = option.options
        .filter(v => item.answerVariants.map(av => av.pattern).includes(v.pattern))
        .map(v => v.key)

      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `b${position}.pattern`,
          logicData: value.includes('TAG.') ? { codebook: [value.split('.').at(-1)], tag: values } : []
        }
      })
    }

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `b${position}.pattern`,
          logicData: null
        }
      })
    }
  }, [item.pattern, item.answerVariants, position, options, dispatch])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `b${position}`,
        error: !item.pattern.length || !item.answerVariants.length
      }
    })
  }, [item.pattern, item.answerVariants, position, dispatch])

  return (
    <>
      <div className="p-composite-group p-fluid p-col-12" data-testid={`stat-dl-cq-batch-${position}`}>
        <Dropdown
          data-testid={`stat-dl-cq-batch-question-${position}`}
          className="p-dropdown-rounded p-col-6"
          placeholder="Select..."
          value={selectedValue}
          options={options}
          optionValue="key"
          optionLabel="text"
          optionGroupLabel="text"
          optionGroupChildren="items"
          optionGroupTemplate={optionGroupTemplate}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
          filter
          filterBy="search,text"
          filterPlaceholder="Search items"
          resetFilterOnHide
          filterInputAutoFocus={false}
          fixedDropdownDir="bottom"
          disabled={disabled}
          onChange={disabled ? null : event => onSelectedChange(event.value)}
        />
        {null === selectedOption ? (
          <MultiSelect
            data-testid={`stat-dl-cq-batch-answers-${position}`}
            className="p-multiselect-rounded p-col-6"
            placeholder="Select..."
            disabled
          />
        ) : (
          <MultiSelect
            data-testid={`stat-dl-cq-batch-answers-${position}`}
            className="p-multiselect-rounded p-col-6"
            placeholder="Select..."
            value={selectedValues}
            options={selectedOption.options}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter
            filterBy="search,text"
            filterPlaceholder="Search items"
            resetFilterOnHide
            fixedDropdownDir="bottom"
            disabled={disabled}
            onChange={disabled ? null : event => onSelectedChange(selectedValue, event.value)}
          />
        )}
      </div>
    </>
  )
}

Batch.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number
}

Batch.displayName = 'Batch'

export default Batch
